import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Zap } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MusicianMatchCard from "@/components/MusicianMatchCard";

export default function SubstituteDialog({ open, onOpenChange, eventId, roles = [], onInvite, express = false, eventInfo = null, onInvited }) {
  const [roleId, setRoleId] = useState(roles[0]?.id || null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const activeRole = roles.find((r) => r.id === roleId) || roles[0] || null;

  const fetchData = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      if (express) {
        const params = { instrument: activeRole?.instrument };
        if (eventInfo?.date) { params.date = eventInfo.date; params.start_time = eventInfo.start_time; params.end_time = eventInfo.end_time; }
        const r = await api.get("/oncall/search", { params });
        const roleCache = Number(activeRole?.cache) || 0;
        const results = (r.data || []).map((m) => ({
          ...m, user_id: m.id, instrument: activeRole?.instrument, cache: roleCache,
          compatibility: undefined, availability: "available", cache_diff: 0,
        }));
        setData({ results, instrument: activeRole?.instrument, role_cache: roleCache });
      } else {
        const r = await api.get(`/events/${eventId}/substitutes`, { params: roleId ? { role_id: roleId } : {} });
        setData(r.data);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [express, eventId, roleId, activeRole?.instrument, activeRole?.cache, eventInfo?.date, eventInfo?.start_time, eventInfo?.end_time]);

  useEffect(() => { if (open) setRoleId(roles[0]?.id || null); /* eslint-disable-next-line */ }, [open]);
  useEffect(() => { if (open) fetchData(); }, [open, roleId, fetchData]);

  const invite = async (m) => {
    try {
      if (express) {
        await api.post("/oncall/invite", { event_id: eventId, user_id: m.user_id, role_id: activeRole?.id });
        toast.success("Convite de Plantão enviado!");
        onInvited && onInvited();
      } else {
        await onInvite(m);
      }
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="substitute-dialog" className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {express ? <Zap className="h-5 w-5 text-[#FF4D00]" /> : <RefreshCw className="h-5 w-5 text-amber-400" />}
            {express ? "Buscar substituto no Plantão" : "Buscar substituto"}
          </DialogTitle>
          <DialogDescription>
            {express
              ? "Músicos de Plantão disponíveis AGORA para assumir a vaga na hora. Convite imediato."
              : "Músicos compatíveis via Match. A diferença de cachê é destacada para sua aprovação."}
          </DialogDescription>
        </DialogHeader>
        {roles.length > 1 && (
          <div className="flex flex-wrap gap-2 shrink-0">
            {roles.map((r) => (
              <button key={r.id} data-testid={`sub-role-${r.id}`} onClick={() => setRoleId(r.id)}
                className={`h-8 px-3 rounded-lg text-xs border transition-colors ${roleId === r.id ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{r.instrument}</button>
            ))}
          </div>
        )}
        <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-2 py-1">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
          {!loading && data && (
            data.results.length === 0
              ? <p className="text-sm text-muted-foreground text-center py-8" data-testid="substitute-empty">
                  {express ? "Nenhum músico de Plantão disponível agora para esta vaga. O raio automático continua ampliando a busca." : "Nenhum substituto compatível. Tente ampliar o raio ou ajustar o cachê."}
                </p>
              : data.results.map((m) => (
                <div key={m.user_id}>
                  <MusicianMatchCard m={m} inviteLabel={express ? "Convidar do Plantão" : "Convidar substituto"} onInvite={invite} />
                  {!express && m.cache_diff ? (
                    <div data-testid={`cache-diff-${m.user_id}`} className={`text-[11px] mt-1 ml-1 flex items-center gap-1 ${m.cache_diff > 0 ? "text-amber-400" : "text-emerald-400"}`}>
                      {m.cache_diff > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      Diferença de cachê: {m.cache_diff > 0 ? "+" : ""}{money(m.cache_diff)} {m.cache_diff > 0 ? "(a aprovar/pagar)" : "(economia)"}
                    </div>
                  ) : null}
                </div>
              ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
